import { ERC20Token, Token } from '@pancakeswap/sdk'
import { WrappedTokenInfo } from '@pancakeswap/token-lists'
import { useMemo, useRef } from 'react'
import { getAddress } from 'viem'
import { erc20ABI, useAccount } from 'wagmi'
import { useAllTokens } from 'hooks/Tokens'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { useInfoSummary } from 'state/infoHub/hooks'
import { useMultipleContractSingleData } from 'state/multicall/hooks'
import { useTokenBalancesWithLoadingIndicator } from 'state/wallet/hooks'
import { useTokenList } from 'views/TokenPages/hooks'
import { HeldRow, rankHeld } from './held'

export type HeldTokens = {
  /** "Your tokens": balance above zero only, ranked by USD value where a price exists, then by amount */
  rows: HeldRow<Token>[]
  /** the token pages list's tokens that no active list carries, as tokens the picker can search and select */
  extraTokens: Token[]
  /** true until the first complete answer (balances, decimals and prices) is in; it never turns back to true for the same wallet */
  loading: boolean
}

const EMPTY: Token[] = []
const BSC = 56

/**
 * The wallet's holdings for the token picker (Bez, 2026-10-08). Sources: the active token lists and the user's imported tokens
 * (useAllTokens, the swap's own map) plus the tokens of the token pages list (/api/tokens/list: CADINU, CBON, competition tokens,
 * tokens with project info, CADINUdex tokens above the liquidity floor). Balances come from the app's multicall hooks (one batched
 * balanceOf per token, the same calls the swap's sort already makes); a token pages token missing from the lists gets its decimals from
 * the CADINUdex Info summary or one batched `decimals` read. Prices: the Info summary's chain-checked price first, then the token pages
 * list's. No new API and no per-token request. `enabled` false reads nothing.
 */
export function useHeldTokens(enabled: boolean): HeldTokens {
  const { address: account } = useAccount()
  const { chainId } = useActiveChainId()
  const on = enabled && Boolean(account) && chainId === BSC
  const allTokens = useAllTokens()
  const { data: list, isLoading: listLoading } = useTokenList(on)
  const { summary, isLoading: summaryLoading } = useInfoSummary(on ? 'cadinu' : null, 'all')

  const stats = useMemo(() => new Map((summary?.tokens.data ?? []).map((s) => [s.address.toLowerCase(), s])), [summary])
  const listed = useMemo(() => new Set(Object.keys(allTokens).map((a) => a.toLowerCase())), [allTokens])
  const missing = useMemo(
    () => (on ? (list?.items ?? []).filter((i) => /^0x[0-9a-fA-F]{40}$/.test(i.address) && !listed.has(i.address.toLowerCase())) : []),
    [on, list, listed],
  )
  const needDecimals = useMemo(() => missing.filter((i) => !stats.has(i.address.toLowerCase())).map((i) => getAddress(i.address)), [missing, stats])
  const decimalsRead = useMultipleContractSingleData({
    abi: erc20ABI,
    addresses: needDecimals,
    functionName: 'decimals',
    options: { enabled: on && needDecimals.length > 0 },
  })

  const extraTokens: Token[] = useMemo(() => {
    if (!missing.length) return EMPTY
    const readDecimals = new Map(needDecimals.map((a, i) => [a.toLowerCase(), decimalsRead[i]?.result]))
    const out: Token[] = []
    for (const i of missing) {
      const key = i.address.toLowerCase()
      const d = stats.get(key)?.decimals ?? readDecimals.get(key)
      if (typeof d !== 'number' && typeof d !== 'bigint') continue
      out.push(
        new WrappedTokenInfo({
          chainId: BSC,
          address: getAddress(i.address),
          decimals: Number(d),
          symbol: i.symbol,
          name: i.name,
          logoURI: i.logoUrl ?? undefined,
        }) as unknown as ERC20Token,
      )
    }
    return out
  }, [missing, needDecimals, decimalsRead, stats])

  const priceOf = useMemo(() => {
    const fromList = new Map((list?.items ?? []).map((i) => [i.address.toLowerCase(), i.priceUsd]))
    return (address: string): number | null => {
      const k = address.toLowerCase()
      return stats.get(k)?.priceUSD ?? fromList.get(k) ?? null
    }
  }, [stats, list])

  const tokens = useMemo(() => (on ? [...Object.values(allTokens), ...extraTokens] : EMPTY), [on, allTokens, extraTokens])
  const [balances, balancesLoading] = useTokenBalancesWithLoadingIndicator(on ? account : undefined, tokens)

  // the rows show once, complete and in their final order: balances read, the token pages tokens' decimals read, both price sources
  // answered (or failed). After that a refresh never brings the placeholders back, so nothing jumps.
  const read = Object.keys(balances).length > 0
  const decimalsPending = needDecimals.length > 0 && decimalsRead.some((c) => c.loading)
  const firstPass = !read || balancesLoading || decimalsPending || listLoading || summaryLoading
  const settled = useRef<{ account?: string; done: boolean }>({ done: false })
  if (settled.current.account !== account) settled.current = { account, done: false }
  if (on && !firstPass) settled.current.done = true
  const loading = on && !settled.current.done

  return useMemo(() => {
    if (!on) return { rows: [], extraTokens: EMPTY, loading: false }
    const rows = rankHeld(
      tokens.map((token) => {
        const b = balances[token.address]
        const amount = b ? Number(b.toExact()) : 0
        const price = priceOf(token.address)
        return { token, amount, usd: price !== null && amount > 0 ? amount * price : null }
      }),
    )
    return { rows, extraTokens, loading }
  }, [on, tokens, balances, priceOf, extraTokens, loading])
}
